import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { useToast } from "hooks/useToast";
import { usePinNoteMutation } from "store/apis/notes";
import { BackendError } from "types/utils/error";

export const ArchiveNote = ({
  isOpen,
  close,
  noteId,
  resetNotes,
  isArchived,
  isPinnedNote,
}: {
  isOpen: boolean;
  close: () => void;
  noteId: string;
  resetNotes: () => void;
  isArchived: boolean;
  isPinnedNote: boolean;
}) => {
  const { alertToast, successToast } = useToast();
  const [pinNote, { isLoading }] = usePinNoteMutation();

  const toggleNoteArchive = async () => {
    try {
      const shouldArchive = !isArchived;
      const unpinPinnedArchiveNote =
        isPinnedNote && shouldArchive ? { is_pinned: false } : {};
      await pinNote({
        noteId,
        is_archived: shouldArchive,
        ...unpinPinnedArchiveNote,
      }).unwrap();

      if (shouldArchive) {
        successToast({
          title: "Archived successfully",
          message: "The note has been moved to archive",
        });
      } else {
        successToast({
          title: "Unarchived successfully",
          message: "The note has been moved to general",
        });
      }
      resetNotes();
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="small">
        <Modal.Body>
          <div>
            <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
              <TriangleWarning />
            </span>
            <p className="t-mb-2 t-text-subtext t-text-text-100 t-mt-5">
              {isArchived ? "Move notes to general?" : "Move note to archive?"}
            </p>
            <div className="t-text-body-sm t-text-text-30">
              {isArchived
                ? "Once unarchived, this note will start appearing in your general notes again."
                : "Once archived, this note won’t appear in your active list but can still be viewed in the archived section."}
            </div>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button type="button">Cancel</Button>
          </Modal.RawClose>
          <Button
            isLoading={isLoading}
            disabled={isLoading}
            customType="primary"
            onClick={toggleNoteArchive}
          >
            {isArchived ? "Unarchive note" : "Archive note"}
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
